import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEOHead from '../../components/SEO/SEOHead';

const FeaturesPageArabic = () => {
  const { i18n } = useTranslation();

  // Set language to Arabic when component mounts
  useEffect(() => {
    if (i18n.language !== 'ar') {
      i18n.changeLanguage('ar');
    }
  }, [i18n]);

  return (
    <>
      <SEOHead 
        title="مميزات إدارة الأسطول - حلول TMS متكاملة المملكة العربية السعودية"
        description="مميزات إدارة أسطول متقدمة: تتبع ذكي للشحنات، احتساب تلقائي للتكاليف، تحليلات الوقود، إدارة السائقين. مصمم لعمليات اللوجستيات السعودية."
        keywords="مميزات إدارة الأسطول، نظام النقل السعودية، تتبع الشحنات، إدارة الوقود، إدارة السائقين، برنامج لوجستيات المملكة، إدارة النقل"
        canonicalUrl="/ar/features"
        lang="ar"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          "name": "برنامج إدارة الأسطول لوجستيات",
          "applicationCategory": "BusinessApplication",
          "operatingSystem": "Web Browser",
          "description": "حل TMS متكامل للمملكة العربية السعودية مع مميزات إدارة أسطول متقدمة",
          "featureList": [
            "تتبع الشحنات وإدارة المسارات",
            "احتساب ذكي لتكلفة الرحلات وإدارة ضريبة القيمة المضافة", 
            "إدارة شاملة للسائقين والمركبات",
            "تحليلات الوقود الذكية",
            "مزامنة عداد المسافات التلقائية",
            "لوحة مقاييس الوقت الفعلي"
          ],
          "offers": {
            "@type": "Offer",
            "price": "2990",
            "priceCurrency": "SAR",
            "priceValidUntil": "2026-12-31",
            "availability": "https://schema.org/InStock",
            "url": "https://logistiat.app/ar/pricing",
            "itemCondition": "https://schema.org/NewCondition",
            "shippingDetails": {
              "@type": "OfferShippingDetails",
              "shippingRate": {
                "@type": "MonetaryAmount",
                "value": "0",
                "currency": "SAR"
              },
              "shippingDestination": {
                "@type": "DefinedRegion",
                "addressCountry": "SA"
              },
              "deliveryTime": {
                "@type": "ShippingDeliveryTime",
                "handlingTime": {
                  "@type": "QuantitativeValue",
                  "minValue": 0,
                  "maxValue": 0,
                  "unitCode": "DAY"
                },
                "transitTime": {
                  "@type": "QuantitativeValue",
                  "minValue": 0,
                  "maxValue": 0,
                  "unitCode": "DAY"
                }
              }
            },
            "hasMerchantReturnPolicy": {
              "@type": "MerchantReturnPolicy",
              "applicableCountry": "SA",
              "returnPolicyCategory": "https://schema.org/MerchantReturnFiniteReturnWindow",
              "merchantReturnDays": 30,
              "returnMethod": "https://schema.org/ReturnByMail",
              "returnFees": "https://schema.org/FreeReturn"
            }
          }
        }}
      />
      <main className="min-h-screen">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center">
              <Link to="/ar" className="text-2xl font-bold text-gray-900">Logistiat</Link>
            </div>
            <div className="hidden md:flex space-x-8 space-x-reverse">
              <Link to="/ar/features" className="text-blue-600 font-medium">المميزات</Link>
              <Link to="/ar/pricing" className="text-gray-600 hover:text-gray-900">الأسعار</Link>
              <Link to="/ar/trip-costing" className="text-gray-600 hover:text-gray-900">احتساب التكلفة</Link>
              <Link to="/ar/contact" className="text-gray-600 hover:text-gray-900">اتصل بنا</Link>
              <Link to="/en" className="text-gray-600 hover:text-gray-900">English</Link>
            </div>
            <div className="flex space-x-4 space-x-reverse">
              <Link
                to="/login"
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 font-medium"
              >
                تسجيل الدخول
              </Link>
            </div>
          </div>
        </nav>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl md:text-6xl font-bold mb-6">
                حلول TMS متكاملة للمملكة العربية السعودية
              </h1>
              <p className="text-xl md:text-2xl mb-8">
                إدارة أسطول متقدمة مع تتبع ذكي للشحنات، احتساب تلقائي للتكاليف، تحليلات الوقود، وإدارة شاملة للسائقين مصممة خصيصاً لعمليات اللوجستيات في المملكة.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/ar/contact"
                  className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 text-center"
                >
                  ابدأ التجربة المجانية
                </Link>
                <Link
                  to="#features"
                  className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-blue-600 text-center"
                >
                  استكشف المميزات
                </Link>
              </div>
            </div>
            <div className="relative">
              <div className="bg-white rounded-xl shadow-2xl p-4">
                <img
                  src="/images/dashboard-screenshot.png"
                  alt="لوحة تحكم إدارة الأسطول Logistiat"
                  className="rounded-lg w-full h-auto"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Features Section */}
      <section id="features" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              مميزات قوية لعمليات الأسطول الحديثة
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              كل ما تحتاجه لإدارة أسطولك بكفاءة، من التتبع في الوقت الفعلي إلى التكامل المحاسبي التلقائي.
            </p>
          </div>

          {/* Shipment Management Feature */}
          <div className="mb-20">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="inline-flex items-center px-4 py-2 bg-blue-100 text-blue-800 rounded-full text-sm font-medium mb-4">
                  🚛 إدارة الشحنات
                </div>
                <h3 className="text-3xl font-bold text-gray-900 mb-6">
                  تتبع متقدم للشحنات وإدارة المسارات
                </h3>
                <div className="space-y-4 mb-6">
                  <div className="flex items-start space-x-3 space-x-reverse">
                    <div className="w-2 h-2 bg-blue-600 rounded-full mt-2"></div>
                    <p className="text-gray-600">تخطيط مسارات متعددة المدن مع كشف تلقائي للمدن في المملكة العربية السعودية</p>
                  </div>
                  <div className="flex items-start space-x-3 space-x-reverse">
                    <div className="w-2 h-2 bg-blue-600 rounded-full mt-2"></div>
                    <p className="text-gray-600">تحديثات الحالة في الوقت الفعلي من الإرسال إلى اكتمال التسليم</p>
                  </div>
                  <div className="flex items-start space-x-3 space-x-reverse">
                    <div className="w-2 h-2 bg-blue-600 rounded-full mt-2"></div>
                    <p className="text-gray-600">تعيين تلقائي للمركبات والسائقين مع كشف التعارضات</p>
                  </div>
                  <div className="flex items-start space-x-3 space-x-reverse">
                    <div className="w-2 h-2 bg-blue-600 rounded-full mt-2"></div>
                    <p className="text-gray-600">إدارة طالبي الخدمة مع تتبع علاقات العملاء</p>
                  </div>
                </div>
              </div>
              <div className="relative">
                <div className="bg-white rounded-xl shadow-lg p-4">
                  <img
                    src="/images/shipment-tracking.png"
                    alt="لوحة تحكم تتبع الشحنات"
                    className="rounded-lg w-full h-auto"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Trip Costing Feature */}
          <div className="mb-20">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="order-2 lg:order-1 relative">
                <div className="bg-white rounded-xl shadow-lg p-4">
                  <img
                    src="/images/trip-costing.png"
                    alt="تحليلات احتساب تكلفة الرحلات"
                    className="rounded-lg w-full h-auto"
                  />
                </div>
              </div>
              <div className="order-1 lg:order-2">
                <div className="inline-flex items-center px-4 py-2 bg-green-100 text-green-800 rounded-full text-sm font-medium mb-4">
                  💰 الإدارة المالية
                </div>
                <h3 className="text-3xl font-bold text-gray-900 mb-6">
                  احتساب ذكي لتكلفة الرحلات وإدارة ضريبة القيمة المضافة
                </h3>
                <div className="space-y-4 mb-6">
                  <div className="flex items-start space-x-3 space-x-reverse">
                    <div className="w-2 h-2 bg-green-600 rounded-full mt-2"></div>
                    <p className="text-gray-600">توزيع تلقائي لتكاليف الوقود مع إمكانيات الإدارة المجمعة</p>
                  </div>
                  <div className="flex items-start space-x-3 space-x-reverse">
                    <div className="w-2 h-2 bg-green-600 rounded-full mt-2"></div>
                    <p className="text-gray-600">حسابات متوافقة مع ضريبة القيمة المضافة مع فصل تلقائي للأساس/الضريبة</p>
                  </div>
                  <div className="flex items-start space-x-3 space-x-reverse">
                    <div className="w-2 h-2 bg-green-600 rounded-full mt-2"></div>
                    <p className="text-gray-600">تحليل هامش الربح في الوقت الفعلي وتفصيل التكاليف</p>
                  </div>
                  <div className="flex items-start space-x-3 space-x-reverse">
                    <div className="w-2 h-2 bg-green-600 rounded-full mt-2"></div>
                    <p className="text-gray-600">قيود يومية تلقائية للتكامل المحاسبي السلس</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Driver Management Feature */}
          <div className="mb-20">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="inline-flex items-center px-4 py-2 bg-purple-100 text-purple-800 rounded-full text-sm font-medium mb-4">
                  👥 عمليات السائقين
                </div>
                <h3 className="text-3xl font-bold text-gray-900 mb-6">
                  إدارة شاملة للسائقين والمركبات
                </h3>
                <div className="space-y-4 mb-6">
                  <div className="flex items-start space-x-3 space-x-reverse">
                    <div className="w-2 h-2 bg-purple-600 rounded-full mt-2"></div>
                    <p className="text-gray-600">ملفات شخصية كاملة للسائقين مع تتبع الأداء والتقييمات</p>
                  </div>
                  <div className="flex items-start space-x-3 space-x-reverse">
                    <div className="w-2 h-2 bg-purple-600 rounded-full mt-2"></div>
                    <p className="text-gray-600">حسابات عمولة تلقائية وإدارة التسويات</p>
                  </div>
                  <div className="flex items-start space-x-3 space-x-reverse">
                    <div className="w-2 h-2 bg-purple-600 rounded-full mt-2"></div>
                    <p className="text-gray-600">جدولة صيانة المركبات مع تتبع عداد المسافات</p>
                  </div>
                  <div className="flex items-start space-x-3 space-x-reverse">
                    <div className="w-2 h-2 bg-purple-600 rounded-full mt-2"></div>
                    <p className="text-gray-600">مطابقة لوحات الترخيص العربية/الإنجليزية وربط المركبات</p>
                  </div>
                </div>
              </div>
              <div className="relative">
                <div className="bg-white rounded-xl shadow-lg p-4">
                  <img
                    src="/images/driver-management.png"
                    alt="نظام إدارة السائقين"
                    className="rounded-lg w-full h-auto"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Advanced Features Grid */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              أتمتة متقدمة وتحليلات
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              مميزات متطورة تميز Logistiat عن أنظمة إدارة الأساطيل التقليدية.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-6">
                <span className="text-2xl">⛽</span>
              </div>
              <h3 className="text-xl font-semibold mb-3">تحليلات الوقود الذكية</h3>
              <p className="text-gray-600 mb-4">استيراد Excel مع مطابقة تلقائية للمركبات، كشف الاحتيال، وتوزيع التكاليف المجمع عبر الشحنات.</p>
              <ul className="text-sm text-gray-500 space-y-1">
                <li>• مطابقة تلقائية للوحات العربية/الإنجليزية</li>
                <li>• خوارزميات كشف احتيال الوقود</li>
                <li>• لوحة تحكم إدارة التكاليف المجمعة</li>
              </ul>
            </div>
            
            <div className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-6">
                <span className="text-2xl">🔄</span>
              </div>
              <h3 className="text-xl font-semibold mb-3">مزامنة عداد المسافات التلقائية</h3>
              <p className="text-gray-600 mb-4">استخراج تلقائي لقراءات عداد المسافات من معاملات الوقود مع التحقق الذكي وتتبع التاريخ.</p>
              <ul className="text-sm text-gray-500 space-y-1">
                <li>• مزامنة الوقود إلى عداد المسافات</li>
                <li>• تنبيهات جدولة الصيانة</li>
                <li>• التحقق من القراءات التاريخية</li>
              </ul>
            </div>
            
            <div className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-6">
                <span className="text-2xl">📊</span>
              </div>
              <h3 className="text-xl font-semibold mb-3">مقاييس الوقت الفعلي</h3>
              <p className="text-gray-600 mb-4">لوحة تحكم مباشرة مع هوامش الربح، تفصيل التكاليف، وتحليلات الأداء لاتخاذ قرارات مدروسة.</p>
              <ul className="text-sm text-gray-500 space-y-1">
                <li>• تحليل الإيرادات مقابل التكاليف</li>
                <li>• مقاييس أداء السائقين</li>
                <li>• تقارير استخدام الأسطول</li>
              </ul>
            </div>
            
            <div className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-6">
                <span className="text-2xl">🏦</span>
              </div>
              <h3 className="text-xl font-semibold mb-3">التكامل المحاسبي</h3>
              <p className="text-gray-600 mb-4">تكامل سلس مع دليل الحسابات، قيود يومية تلقائية، وتقارير متوافقة مع ضريبة القيمة المضافة.</p>
              <ul className="text-sm text-gray-500 space-y-1">
                <li>• قيود يومية تلقائية</li>
                <li>• حساب وتقرير ضريبة القيمة المضافة</li>
                <li>• تصنيف المصروفات</li>
              </ul>
            </div>
            
            <div className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-6">
                <span className="text-2xl">🌐</span>
              </div>
              <h3 className="text-xl font-semibold mb-3">دعم ثنائي اللغة</h3>
              <p className="text-gray-600 mb-4">واجهة كاملة بالعربية والإنجليزية مع معالجة ذكية للنصوص وتوطين ثقافي للسوق السعودي.</p>
              <ul className="text-sm text-gray-500 space-y-1">
                <li>• تبديل واجهة عربية/إنجليزية</li>
                <li>• تنسيق تاريخ ثقافي</li>
                <li>• قواعد عمل محلية</li>
              </ul>
            </div>
            
            <div className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center mb-6">
                <span className="text-2xl">🔒</span>
              </div>
              <h3 className="text-xl font-semibold mb-3">أمان المؤسسات</h3>
              <p className="text-gray-600 mb-4">عزل متعدد الشركات، تحكم في الوصول القائم على الأدوار، ومسارات تدقيق شاملة لامتثال المؤسسات.</p>
              <ul className="text-sm text-gray-500 space-y-1">
                <li>• عزل بيانات الشركة</li>
                <li>• إدارة أدوار المستخدمين</li>
                <li>• سجلات تدقيق النشاط</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Integration & Automation Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              مصمم لصناعة اللوجستيات في المملكة العربية السعودية
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              مصمم خصيصاً لأنظمة المملكة، الممارسات التجارية، والمتطلبات التشغيلية.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-white text-2xl">🇸🇦</span>
              </div>
              <h3 className="text-xl font-semibold mb-3">امتثال المملكة</h3>
              <p className="text-gray-600">أنظمة ضريبة القيمة المضافة، التوثيق العربي، وامتثال الممارسات التجارية المحلية مدمج.</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-white text-2xl">⚡</span>
              </div>
              <h3 className="text-xl font-semibold mb-3">نشر سريع</h3>
              <p className="text-gray-600">إعداد سريع مع مدن سعودية مُعدة مسبقاً، أنواع الشاحنات، وسير العمل التجاري.</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-white text-2xl">📱</span>
              </div>
              <h3 className="text-xl font-semibold mb-3">جاهز للجوال</h3>
              <p className="text-gray-600">تصميم متجاوب محسن للأجهزة اللوحية والجوالات المستخدمة من قبل السائقين والمرسلين.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">مستعد لتحويل عمليات أسطولك؟</h2>
          <p className="text-xl mb-8">
            انضم لشركات اللوجستيات الرائدة في المملكة العربية السعودية التي قامت بتبسيط عملياتها مع حل TMS الشامل من Logistiat.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/ar/contact"
              className="bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 text-lg"
            >
              ابدأ تجربتك المجانية
            </Link>
            <Link
              to="/ar/pricing"
              className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-blue-600 text-lg"
            >
              عرض خطط الأسعار
            </Link>
          </div>
          <p className="text-blue-200 mt-6">
            بدون رسوم إعداد • تجربة مجانية 30 يوم • إلغاء في أي وقت
          </p>
        </div>
      </section>
      </main>
    </>
  );
};

export default FeaturesPageArabic;